<script setup>
import { ref, computed, onMounted } from 'vue';
import { router } from '@inertiajs/vue3';
import { storeToRefs } from 'pinia';
import { useRuleSetStore } from '@/stores/ruleSet';

const store = useRuleSetStore();
const { search, getForm } = store;
const { all, form } = storeToRefs(store);

const params = ref({
    term: '',
    product_configuration_id: null,
    page: 1,
    sort_by: null,
    per_page: 10,
});

onMounted(async () => {
    if (!form.value?.products?.length) {
        await getForm();
    }
});

const products = computed(() => form.value?.products ?? []);

const searchItems = () => search({ ...params.value });

const clearSearch = () => {
    params.value.term = '';
    searchItems();
};

const goCreate = () => router.visit('/rule-engine/create');
const goView = (item) => router.visit(`/rule-engine/${item.hash}`);
const goClone = (item) => router.visit(`/rule-engine/${item.hash}/clone`);

const tableUpdate = ({ page, sortBy, itemsPerPage }) => {
    Object.assign(params.value, { page, sort_by: sortBy, per_page: itemsPerPage });
    searchItems();
};

const formatMST = (utcDate) => {
    if (!utcDate) {
        return '';
    }
    const date = new Date(utcDate);
    return new Intl.DateTimeFormat('en-CA', {
        timeZone: 'America/Edmonton',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
    }).format(date) + ' (MST)';
};

const formatRuleSetLabels = (labels) => {
    if (!Array.isArray(labels) || labels.length === 0) {
        return '';
    }

    return labels
        .map(label => label.name)
        .join(', ');
};

</script>

<template>
<MainContentLayout>
    <template #breadcrumb><BreadCrumb /></template>
    <template #title>{{ $t('label.rule_engine') }}</template>

    <template #actions>
        <StyledButton @click="goCreate">
            {{ $t('label.new_rule_set') }}
        </StyledButton>
    </template>

    <div class="grid lg:grid-cols-3 md:gap-4 md:mb-4">
        <StyledSearch
            v-model="params.term"
            v-debounce:500ms="searchItems"
            name="search"
            :label="$t('label.rule_sets_search')"
            :debounce-events="['input']"
            @click:clear="clearSearch" />

        <StyledDropdown
            v-model="params.product_configuration_id"
            clearable
            :placeholder="$t('label.product_filter')"
            :options="products"
            item-title="title"
            item-value="id"
            name="product_configuration_id"
            @input="searchItems" />

        <div />
    </div>

    <DataTable
        v-if="all"
        :data="all"
        :headers="[
            { title: $t('label.product_configuration'), key: 'product', value: i => i.product_configuration?.title ?? '', sortable: false },
            { title: $t('label.rule_set_title'), key: 'title', value: i => i.title ?? '', sortable: false },
            { title: $t('label.labels'), key: 'labels', value: i => formatRuleSetLabels(i.labels), sortable: false},
            { title: $t('label.version'), key: 'version', value: i => i.version ?? '', sortable: false },
            { title: $t('label.last_updated'), key: 'updated_at', value: i => formatMST(i.updated_at), sortable: false },
            { title: $t('label.status'), key: 'is_active', value: i => i.is_active ? $t('label.active') : $t('label.inactive'), sortable: false },
        ]"
        :actions="[
            { title: $t('label.view'), icon: 'eye', action: goView },
            { title: $t('label.clone'), icon: 'copy', action: goClone },
        ]"
        @table-update="tableUpdate" />
</MainContentLayout>
</template>
